/**
 * Moov's $0.01 verification deposit carries the code in the transaction
 * description, e.g. `FROM: MV3759|VCU FBO Moov Customers/MV3759|SU REF:`.
 *
 * Matches `MV` + 4 digits that isn't glued to a preceding letter or digit
 * (so reference numbers like `…P1BMV0188…` are ignored), tolerating common
 * OCR misreads:
 * - a doubled or spaced prefix (`MVv3759`, `M V 3759`) or a hyphen (`MV-3759`)
 * - letters read in place of digits (O→0, I/L→1, S→5, B→8, …), as long as at
 *   least one real digit is present, so words like `MV SOLD` aren't codes
 * - the `|` right after the code read as `I` or `1` (`MV3759IVCU`)
 */
// The preceding character is matched instead of using a lookbehind, which
// throws a SyntaxError on Safari/iOS below 16.4 (we support iOS 12+).
const MV_CODE_PATTERN =
  /(?:^|[^A-Z0-9])M ?VV? ?-? ?([0-9OQDILSBZ]{4})(?=$|[^0-9]|[1I][A-Z])/g;

const DIGIT_LOOKALIKES: Record<string, string> = {
  O: '0',
  Q: '0',
  D: '0',
  I: '1',
  L: '1',
  S: '5',
  B: '8',
  Z: '2',
};

/** Returns the distinct 4-digit MV codes found in OCR text, in reading order. */
export function findMvCodes(text: string): string[] {
  const normalizedText = text.toUpperCase().replace(/[|¦]/g, ' ');
  const codes = new Set<string>();

  MV_CODE_PATTERN.lastIndex = 0;

  for (
    let match = MV_CODE_PATTERN.exec(normalizedText);
    match;
    match = MV_CODE_PATTERN.exec(normalizedText)
  ) {
    const rawCode = match[1] ?? '';

    if (/[0-9]/.test(rawCode)) {
      codes.add(
        rawCode.replace(/[A-Z]/g, (char) => DIGIT_LOOKALIKES[char] ?? char),
      );
    }
  }

  return [...codes];
}
